import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { leaderboardService } from "@/services";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Trophy, Star, TrendingUp, Crown } from "lucide-react";
import AccountNavMenu from "@/components/AccountNavMenu";
import PlayerNameLink from "@/components/PlayerNameLink";

const LEVEL_COLORS = {
  Beginner: "bg-gray-100 text-gray-700",
  Trader: "bg-blue-100 text-blue-700",
  Whale: "bg-purple-100 text-purple-700",
  "Market Master": "bg-amber-100 text-amber-700",
};

const RANK_COLORS = ["text-[#FFC107]", "text-[#9E9E9E]", "text-[#CD7F32]"];

export default function LeaderboardPage() {
  const { t } = useTranslation();
  usePageTitle("Leaderboard");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    leaderboardService.getGlobal({ limit: 50 })
      .then((data) => { setLeaders(data.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const currentUserId = user?.user_id;

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8" id="main-content" role="main" tabIndex={-1} data-testid="leaderboard-page">
      <div className="max-w-3xl mx-auto">
        {/* Two-row header on mobile (back+menu on top, title below)
            keeps each piece roomy. ≥sm collapses back to a single
            row with everything in line. */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("leaderboard.navigation.back")}
          </Button>
          <AccountNavMenu current="leaderboard" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2 mb-6" style={{ fontFamily: "Fredoka" }}>
          <Trophy className="w-7 h-7 text-[#FFC107] shrink-0" /> {t("leaderboard.title")}
        </h1>

        {/* Top 3 Podium — scannable highlight */}
        {!loading && leaders.length >= 1 && (
          <div className="grid grid-cols-3 gap-3 mb-5" data-testid="leaderboard-podium">
            {[1, 0, 2].map((rankIdx) => {
              const p = leaders[rankIdx];
              if (!p) return <div key={`podium-empty-${rankIdx}`} />;
              const tint = rankIdx === 0
                ? "from-[#FFC107]/40 to-[#FFC107]/10 border-[#FFC107]/50"
                : rankIdx === 1
                ? "from-gray-300/50 to-gray-100/20 border-gray-300/60"
                : "from-orange-300/40 to-orange-100/20 border-orange-300/60";
              const iconColor = RANK_COLORS[rankIdx];
              const podiumHeight = rankIdx === 0 ? "sm:h-36" : rankIdx === 1 ? "sm:h-32" : "sm:h-28";
              return (
                <div key={`podium-${rankIdx}`}
                  className={`rounded-3xl bg-gradient-to-b ${tint} border-2 backdrop-blur-xl shadow-lg p-4 text-center flex flex-col items-center justify-end ${podiumHeight}`}
                  data-testid={`podium-${rankIdx}`}>
                  <Crown className={`w-6 h-6 ${iconColor} mb-1`} />
                  <p className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD] truncate w-full">{p.name}</p>
                  <p className="text-[10px] text-[#1A237E]/60 dark:text-[#E3F2FD]/60">{t("leaderboard.podium.lvl", { level: p.level, xp: p.xp })}</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#1A237E]/5 dark:bg-[#E3F2FD]/5 border-b border-[#1A237E]/10 dark:border-[#E3F2FD]/10 text-xs font-bold text-[#1A237E]/60 dark:text-[#E3F2FD]/60 uppercase tracking-wider">
            <div className="col-span-1">{t("leaderboard.column.rank")}</div>
            <div className="col-span-4">{t("leaderboard.column.player")}</div>
            <div className="col-span-2 text-center">{t("leaderboard.column.level")}</div>
            <div className="col-span-2 text-center">{t("leaderboard.column.xp")}</div>
            <div className="col-span-1 text-center">{t("leaderboard.column.wins")}</div>
            <div className="col-span-2 text-center">{t("leaderboard.column.best")}</div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-[#1A237E]/40 dark:text-[#E3F2FD]/40 animate-pulse">{t("leaderboard.loading")}</div>
          ) : leaders.length === 0 ? (
            <div className="p-10 text-center text-[#1A237E]/40 dark:text-[#E3F2FD]/40">{t("leaderboard.empty")}</div>
          ) : (
            <div className="divide-y divide-[#1A237E]/5">
              {leaders.map((p, i) => {
                const isMe = currentUserId && p.user_id === currentUserId;
                const topTint = i === 0
                  ? "bg-[#FFC107]/10"
                  : i === 1
                  ? "bg-gray-200/40"
                  : i === 2
                  ? "bg-orange-200/20"
                  : "";
                return (
                  <div key={p.user_id}
                    className={`grid grid-cols-12 gap-2 px-4 py-4 items-center transition-colors ${topTint} ${
                      isMe ? "ring-2 ring-[#38B6FF] ring-inset bg-[#38B6FF]/10" : "hover:bg-white/50 dark:bg-slate-900/50"
                    }`}
                    data-testid={`leaderboard-row-${i}`}>
                    <div className="col-span-1">
                      {i < 3 ? (
                        <Crown className={`w-5 h-5 ${RANK_COLORS[i]}`} />
                      ) : (
                        <span className="text-sm font-bold text-[#1A237E]/40 dark:text-[#E3F2FD]/40">{i + 1}</span>
                      )}
                    </div>
                    <div className="col-span-4 min-w-0">
                      <p className="text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD] truncate flex items-center gap-2">
                        <PlayerNameLink
                          userId={p.user_id}
                          name={p.name}
                          className="font-bold truncate"
                          testId={`leaderboard-name-${i}`}
                        />
                        {isMe && <span className="text-[10px] bg-[#38B6FF] text-white rounded-full px-2 py-0.5 font-bold">{t("leaderboard.you")}</span>}
                      </p>
                    </div>
                    <div className="col-span-2 text-center">
                      <Badge className={`text-[10px] ${LEVEL_COLORS[p.level_name] || "bg-gray-100"}`}>
                        {p.level_name}
                      </Badge>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="text-sm font-bold text-[#38B6FF]">{p.xp}</span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm font-semibold text-[#1A237E] dark:text-[#E3F2FD]">{p.wins}</span>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="text-sm font-semibold text-[#00E676]">${p.highest_portfolio}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
